'use client';

import { createAvatar } from '@usespaceui/squishmoji';
import { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { MotionConfigContext } from 'motion/react';
import { useAgentActivityInView } from './use-agent-activity-in-view';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useSubAgentMorph } from './use-sub-agent-morph';
import styles from './sub-agent-avatar.module.css';

/** Seeded catalog picks give each worker a stable shape, palette, and fill style. */
export function SubAgentAvatar({
  identity,
  working = false,
  sleeping = false,
  size = 20,
}: {
  identity?: string;
  working?: boolean;
  sleeping?: boolean;
  size?: number;
}) {
  const animated = working || sleeping;
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useAgentActivityInView(ref, animated);
  const motion = useContext(MotionConfigContext);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!animated) return;
    const update = () => setVisible(document.visibilityState === 'visible');
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, [animated]);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const running =
    animated &&
    inView &&
    visible &&
    !reduceMotion &&
    motion.reducedMotion !== 'always';
  const instanceId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const seed = identity || instanceId;
  const avatar = useMemo(
    () =>
      createAvatar(`imai-worker:${seed}`, {
        shape: 'all',
        expression: 'attentive',
        backgroundStyle: 'all',
        size: 20,
        format: 'json',
      }),
    [seed]
  );
  const svg = useMemo(() => {
    const markup = avatar.svg;
    // The same worker can appear in a group header and a detail row.
    return markup.replace(/sq-[a-z0-9]+(?=-)/g, (id) => `${instanceId}-${id}`);
  }, [avatar, instanceId]);
  useSubAgentMorph(ref, avatar, running);
  return (
    <span
      ref={ref}
      aria-hidden="true"
      data-worker-working={working}
      data-worker-sleeping={sleeping && !working}
      data-worker-animation={running ? 'running' : 'paused'}
      data-sub-agent-avatar={seed}
      className={`${styles.avatar} inline-flex shrink-0 items-center justify-center`}
      style={{ width: size, height: size }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
